import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Icon } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { ReturnDetail } from '@/components/organisms/Return';
import {
  useReturnDetail,
  useApproveReturnMutation,
  useRejectReturnMutation,
  useProcessRefundMutation,
} from '@/hooks/useErpApi';
import { updateReturnStatus } from '@/features/returns';
import type { AppDispatch } from '@/store';

export default function ReturnDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [refundData, setRefundData] = useState({
    refund_method: 'original_payment',
    notes: '',
  });

  const { data: returnData, isLoading, error, refetch } = useReturnDetail(id || '');
  const { mutate: approveReturn, isLoading: isApproving } = useApproveReturnMutation();
  const { mutate: rejectReturn, isLoading: isRejecting } = useRejectReturnMutation();
  const { mutate: processRefund, isLoading: isProcessingRefund } = useProcessRefundMutation();

  const handleApprove = () => {
    setIsApproveModalOpen(true);
  };

  const handleReject = () => {
    setIsRejectModalOpen(true);
  };

  const handleProcessRefund = () => {
    setIsRefundModalOpen(true);
  };

  const confirmApprove = async () => {
    if (!id) return;

    try {
      await approveReturn({ id, notes: approvalNotes });
      dispatch(updateReturnStatus({ id, status: 'approved' }));
      setIsApproveModalOpen(false);
      setApprovalNotes('');
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to approve return:', error);
      alert('Failed to approve return. Please try again.');
    }
  };

  const confirmReject = async () => {
    if (!id || !rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await rejectReturn({ id, reason: rejectReason });
      dispatch(updateReturnStatus({ id, status: 'rejected' }));
      setIsRejectModalOpen(false);
      setRejectReason('');
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to reject return:', error);
      alert('Failed to reject return. Please try again.');
    }
  };

  const confirmProcessRefund = async () => {
    if (!id || !returnData) return;

    try {
      await processRefund({
        id,
        data: {
          refund_amount: returnData.total_refund,
          refund_method: refundData.refund_method,
          notes: refundData.notes,
        },
      });
      dispatch(updateReturnStatus({ id, status: 'refunded' }));
      setIsRefundModalOpen(false);
      setRefundData({ refund_method: 'original_payment', notes: '' });
      refetch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to process refund:', error);
      alert('Failed to process refund. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Icon name="CircleAlert" className="mx-auto h-12 w-12 text-red-600 mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Failed to Load Return</h3>
          <p className="text-red-700 mb-4">{error.message}</p>
          <div className="flex justify-center space-x-4">
            <Button onClick={() => refetch()}>Try Again</Button>
            <Button variant="outline" onClick={() => navigate('/returns')}>
              Back to Returns
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !returnData) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between items-center">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/returns')}>
            <Icon name="ArrowLeft" className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Return Details</h1>
            <p className="text-gray-600 mt-1">View and manage return #{id?.substring(0, 8)}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => window.print()}>
            <Icon name="Printer" className="mr-2 h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      {/* Return Details Component */}
      <ReturnDetail
        returnData={returnData}
        onApprove={handleApprove}
        onReject={handleReject}
        onProcessRefund={handleProcessRefund}
        isLoading={isLoading}
      />

      {/* Approve Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Approve Return"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to approve this return? This will adjust the inventory and allow
            refund processing.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Notes (Optional)
            </label>
            <textarea
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this approval"
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => setIsApproveModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmApprove} disabled={isApproving}>
              {isApproving ? 'Approving...' : 'Approve Return'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Reject Return"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to reject this return? This action cannot be undone.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Explain why this return is being rejected"
              required
            />
          </div>
          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => setIsRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="error"
              onClick={confirmReject}
              disabled={isRejecting || !rejectReason.trim()}
            >
              {isRejecting ? 'Rejecting...' : 'Reject Return'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Process Refund Modal */}
      <Modal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        title="Process Refund"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900">
              Refund Amount: ₹{returnData.total_refund.toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refund Method <span className="text-red-500">*</span>
            </label>
            <select
              value={refundData.refund_method}
              onChange={(e) => setRefundData({ ...refundData, refund_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="original_payment">Original Payment Method</option>
              <option value="store_credit">Store Credit</option>
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Refund Notes (Optional)
            </label>
            <textarea
              value={refundData.notes}
              onChange={(e) => setRefundData({ ...refundData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this refund"
            />
          </div>

          <div className="flex justify-end space-x-4">
            <Button variant="outline" onClick={() => setIsRefundModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={confirmProcessRefund} disabled={isProcessingRefund}>
              {isProcessingRefund ? 'Processing...' : 'Process Refund'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
